import React, { useEffect, useState } from "react";
import { design, T } from "../../../utils/theme";
import { urgencyColor, elapsedLabel } from "../../../utils/helpers";
import { Card, CardBody, CardFooter, CardHeader } from "reactstrap";
import KDSService from "../../../services/OrderingServices/KDSService";
import toast from "react-hot-toast";
import { calculateElapsedSeconds } from "../../../utils/kdsTransformer";
import RadialClock from "./RadialClock";

export default function OrderCard({ order, fetchOrderList }) {
  const [loading, setLoading] = useState(false);

  // Calculate elapsed time dynamically
  const elapsed = calculateElapsedSeconds(order.createdAt);
  const MAX_TIME = 20 * 60; // 20 minutes
  const pct = Math.min((elapsed / MAX_TIME) * 100, 100);
  const barColor = urgencyColor(elapsed, MAX_TIME);
const urgency = pct > 70 ? "URGENT" : pct > 40 ? "MID" : "OK";

  const typeIcon =
    {
      "Dine-in": "🍽️",
      Takeout: "📦",
      Delivery: "🚗",
    }[order.type] || "🍽️";

  // Single unified handler for both prepare and serve
  const handlePrepareOrServe = async (itemId, productName) => {
    try {
      setLoading(true);
      const item = order.items.find((i) => i.orderDetailId === itemId);

      if (!item) {
        toast.error("Item not found");
        return;
      }

      // If not prepared yet, mark as prepared
      if (!item.preparedAt) {
        await KDSService.markPrepare(itemId);
        toast.success(`${productName} marked as prepared`);
      }
      // If prepared but not served, mark as served
      else if (item.preparedAt && !item.servedAt) {
        await KDSService.markServe(itemId);
        toast.success(`${productName} marked as served`);
      }

      await fetchOrderList();
    } catch (error) {
      console.error("Error updating item status:", error);
      toast.error(error.message || "Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  const useElapsed = (createdAt) => {
  const [state, setState] = useState({ label: "00:00", seconds: 0 });

  useEffect(() => {
    const tick = () => {
      const diff = Math.floor((Date.now() - new Date(createdAt)) / 1000);
      const m = Math.floor(diff / 60).toString().padStart(2, "0");
      const s = (diff % 60).toString().padStart(2, "0");
      setState({ label: `${m}:${s}`, seconds: diff });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [createdAt]);

  return state; // { label, seconds }
};

// Usage in OrderCard:
const { label: elapsedS, seconds: elapsedSeconds } = useElapsed(order.createdAt);

  

  return (
   <Card
 style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.12)", borderRadius: 12, overflow: "hidden" }}
>
  {/* ─── HEADER ─── */}
  <div
    style={{
      padding: "8px 12px",
      background: T.surfaceAlt,
      borderBottom: `1px solid ${T.border}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <div style={{ fontWeight: 700, fontSize: "14px" }}>#{order.id}</div>
    <div style={{ fontSize: "11px", color: T.textMuted }}>{order.table}</div>
  </div>

  {/* ─── TIMER BAR ─── */}
 <div
  style={{
    padding: "7px 12px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    borderBottom: `0.5px solid ${T.border}`,
    background: T.surface,
  }}
>
  <RadialClock elapsed={elapsedSeconds} maxMinutes={20} />

  {/* <span
    style={{
      fontSize: 11,
      fontFamily: "monospace",
      color: T.textMuted,
    }}
  >
    {elapsed}
  </span> */}

  <span
    style={{
      marginLeft: "auto",
      fontSize: 10,
      fontWeight: 600,
      padding: "2px 8px",
      borderRadius: 20,
      letterSpacing: "0.4px",
      ...(urgency === "URGENT"
        ? { background: "#fcebeb", color: "#a32d2d" }
        : urgency === "MID"
        ? { background: "#faeeda", color: "#854f0b" }
        : { background: "#eaf3de", color: "#3b6d11" }),
    }}
  >
    {urgency}
  </span>
</div>

  {/* ─── TIME INFO ─── */}
  {/* <div
    style={{
      fontSize: "11px",
      padding: "4px 10px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      background: T.surface,
    }}
  >
    <span style={{ fontFamily: "monospace", display: "flex", alignItems: "center", gap: 4 }}>
      <i className="ri-timer-line" style={{ fontSize: "13px" }} />
      {elapsed}
    </span>
    <span style={{ fontWeight: 600 }}>
      {pct > 70 ? "URGENT" : pct > 40 ? "MID" : "OK"}
    </span>
  </div> */}

  {/* ─── ITEMS LIST ─── */}
  <div
    style={{
      flex: 1,
      overflowY: "auto",
      maxHeight: "220px",
    }}
  >
    {order.items.map((item, i) => {
      const isPrepared = !!item.preparedAt;
      const isServed = !!item.servedAt;

      return (
        <div
          key={item.orderDetailId || i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 10px",
            borderBottom: `1px solid ${T.border}`,
            fontSize: "13px",
            background: isServed
              ? T.greenLight
              : isPrepared
              ? `${T.yellow}22`
              : "transparent",
          }}
        >
          {/* ITEM NAME */}
          <div
            style={{
              flex: 1,
              textDecoration: isServed ? "line-through" : "none",
              color: isServed ? T.textMuted : T.textPrimary,
            }}
          >
            {item.productName}
          </div>

          {/* QTY */}
          <div
            style={{
              fontWeight: 700,
              minWidth: 28,
              textAlign: "right",
              color: T.orange,
            }}
          >
            ×{item.quantity}
          </div>

          {/* ACTION BUTTON */}
          {!isServed ? (
            <button
              onClick={() =>
                handlePrepareOrServe(
                  item.orderDetailId,
                  isPrepared ? "served" : "prepared",
                )
              }
              disabled={loading}
              style={{
                fontSize: "11px",
                padding: "4px 8px",
                borderRadius: 6,
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: 600,
                background: isPrepared ? T.green : T.orange,
                color: "#fff",
                whiteSpace: "nowrap",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {isPrepared ? "Serve" : "Prepare"}
            </button>
          ) : (
            // ✅ #4 Remix icon instead of ✅ emoji
            <i className="ri-checkbox-circle-fill" style={{ fontSize: "16px", color: T.green }} />
          )}
        </div>
      );
    })}
  </div>
</Card>
  );
}
